import { calibrateFromResponse } from '@/utils/server-time'
import { isSignatureEnabled } from '@/utils/api-signature'

/**
 * 回應攔截器：從每個回應信封頂層的 serverTime 校準本機時鐘。
 *
 * serverTime 與 code / data 同級（拿回應物件是 res.data.serverTime，拿到的若已是回應體則是
 * res.serverTime，兩種取法都認；秒級），成功與失敗的回應都有；
 * 後端要求 X-Timestamp 取它，不要前端自己生成，所以這裡只做記錄，不改動回應內容，
 * 也不寫任何日誌（避免洩漏認證細節）。沒帶 serverTime 的回應（例如第三方介面）直接忽略。
 */

/** 認證失敗的狀態碼：可能是時間戳超出時效窗口，校準到伺服器時間後值得重試一次 */
const AUTH_FAILURE_STATUS = [401, 403]

/** 標記「這個請求已經重試過」，避免無限重試 */
const RETRY_FLAG = '__serverTimeRetry'

/**
 * 成功回應：先校準時間，再原樣返回
 * @param {Object} response axios 回應
 * @returns {Object} 同一個 response
 */
export function onResponse(response) {
  calibrateFromResponse(response)
  return response
}

/**
 * 失敗回應：一樣先嘗試校準（失敗回應的頂層同樣帶 serverTime）；
 * 若這次失敗是 401 / 403（很可能是時間戳超窗）而且剛好校準到了伺服器時間，
 * 就用新的時間戳與新的 nonce 重試一次。
 *
 * 重試必須重新生成整套請求頭，所以這裡把 config 交回 axios 重跑，
 * 由請求攔截器重新計算 X-Timestamp / X-Nonce / X-Signature；
 * 只有「瀏覽器自行簽名」的應急鏈路才需要（主鏈路由 BFF 代簽名並在 BFF 內重試）。
 *
 * @param {Object} error axios 錯誤物件
 * @param {Function} retry 重試函式 (config) => Promise，預設 (config) => axios(config)
 * @returns {Promise} 重試結果或原始的 reject
 */
export function onResponseError(error, retry) {
  const response = error && error.response
  if (!response) {
    // 網路層錯誤（沒有回應）沒有 serverTime 可以校準
    return Promise.reject(error)
  }

  const calibrated = calibrateFromResponse(response)
  const config = error.config
  const status = response.status

  if (
    calibrated &&
    config &&
    !config[RETRY_FLAG] &&
    AUTH_FAILURE_STATUS.indexOf(status) !== -1 &&
    typeof retry === 'function' &&
    isSignatureEnabled()
  ) {
    config[RETRY_FLAG] = true
    return retry(config)
  }

  return Promise.reject(error)
}
